import {
  GlobalSearchResult,
  NavigationSearchResult,
} from '@/api/global-search';
import { Asset } from '@/asset/api/types';
import { OrganizationDefaultAssetMarker } from '@/components/organization-default-asset-marker';
import {
  ApartmentRounded,
  ChecklistRtlOutlined,
  InventoryOutlined,
  KeyboardReturnOutlined,
  LocalShippingOutlined,
  PlaceRounded,
  ShoppingCartOutlined,
  WidgetsOutlined,
} from '@/constants/hardfin-standard-icons';
import { getProjectStatusLabel } from '@/constants/project-status';
import { Customer } from '@/customer/api/types';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { Location } from '@/location/api/types';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { Project } from '@/project/api/types';
import { PurchaseOrder } from '@/purchase-order/api/types';
import {
  RETURN_ORDER_REASON_LABELS,
  ReturnOrder,
} from '@/return-order/api/types';
import { Shipment } from '@/shipment/api/types';
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { MouseEvent, ReactNode, cloneElement, useEffect, useRef } from 'react';

interface SearchResultItemProps {
  itemKey: string;
  url: string;
  icon: ReactNode;
  primary: string;
  secondary?: string | null;
  isSelected: boolean;
  selectedItemRef: React.RefObject<HTMLDivElement>;
  onClick: (url: string, event: MouseEvent) => void;
}

function SearchResultItem(props: SearchResultItemProps) {
  return (
    <ListItem disablePadding key={props.itemKey}>
      <ListItemButton
        component="div"
        onClick={(e) => props.onClick(props.url, e)}
        selected={props.isSelected}
        ref={props.isSelected ? props.selectedItemRef : undefined}
      >
        <ListItemAvatar
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 32,
              height: 32,
            }}
          >
            {props.icon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={props.primary} secondary={props.secondary} />
      </ListItemButton>
    </ListItem>
  );
}

interface SearchResultSectionProps {
  title: string;
  children: ReactNode;
}

function SearchResultSection(props: SearchResultSectionProps) {
  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 'bold',
          padding: (theme) => theme.spacing(1, 2),
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        {props.title}
      </Typography>
      {props.children}
    </Box>
  );
}

/**
 * Props for GlobalSearchResultsList
 * @typedef {Object} GlobalSearchResultsListProps
 * @property {GlobalSearchResult} entityResults - Array of entity result groups to display
 * @property {(url: string, event: React.MouseEvent) => void} handleResultClick - Click handler for result items
 * @property {number} maxResults - Maximum results to show per group
 * @property {number} selectedIndex - Index of the currently selected item
 */
interface GlobalSearchResultsListProps {
  entityResults: GlobalSearchResult;
  handleResultClick: (url: string, event: MouseEvent) => void;
  maxResults: number;
  selectedIndex?: number;
}

/**
 * Renders the grouped search results list for the global search component.
 * Each group is displayed with a header and a list of clickable results.
 * Includes auto-scrolling functionality to keep the selected item in view.
 */
export function GlobalSearchResultsList({
  entityResults,
  handleResultClick,
  maxResults,
  selectedIndex = -1,
}: GlobalSearchResultsListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);
  let currentIndex = 0;

  // Scroll selected item into view when selection changes
  useEffect(() => {
    if (selectedIndex >= 0 && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  return (
    <List dense disablePadding ref={listRef}>
      {entityResults.navigation.length > 0 && (
        <SearchResultSection title="Navigation">
          {entityResults.navigation
            .slice(0, maxResults)
            .map((navItem: NavigationSearchResult) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={navItem.to}
                  itemKey={navItem.to}
                  url={navItem.to}
                  icon={cloneElement(navItem.icon, { fontSize: 'small' })}
                  primary={navItem.label}
                  secondary={navItem.sectionLabel}
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.assets.length > 0 && (
        <SearchResultSection title="Assets">
          {entityResults.assets.slice(0, maxResults).map((asset: Asset) => {
            const index = currentIndex++;
            return (
              <SearchResultItem
                key={`assets-${asset.key}`}
                itemKey={`assets-${asset.key}`}
                url={`/assets/${asset.key}`}
                icon={
                  <OrganizationDefaultAssetMarker
                    iconProps={{ fontSize: 'small' }}
                  />
                }
                primary={asset.serial}
                secondary={asset.item?.name || asset.description}
                isSelected={selectedIndex === index}
                selectedItemRef={selectedItemRef}
                onClick={handleResultClick}
              />
            );
          })}
        </SearchResultSection>
      )}
      {entityResults.locations.length > 0 && (
        <SearchResultSection title="Locations">
          {entityResults.locations
            .slice(0, maxResults)
            .map((location: Location) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`locations-${location.key}`}
                  itemKey={`locations-${location.key}`}
                  url={`/locations/${location.key}`}
                  icon={<PlaceRounded fontSize="small" />}
                  primary={getEffectiveLocationName(location)}
                  secondary={
                    location.customer?.name ||
                    location.address?.formattedAddress
                  }
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.customers.length > 0 && (
        <SearchResultSection title="Customers">
          {entityResults.customers
            .slice(0, maxResults)
            .map((customer: Customer) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`customers-${customer.key}`}
                  itemKey={`customers-${customer.key}`}
                  url={`/customers/${customer.key}`}
                  icon={<ApartmentRounded fontSize="small" />}
                  primary={customer.name}
                  secondary={
                    customer.billingContact?.name || customer.billingAddress
                  }
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.projects.length > 0 && (
        <SearchResultSection title="Projects">
          {entityResults.projects
            .slice(0, maxResults)
            .map((project: Project) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`projects-${project.key}`}
                  itemKey={`projects-${project.key}`}
                  url={`/projects/${project.key}`}
                  icon={<WidgetsOutlined fontSize="small" />}
                  primary={project.name}
                  secondary={
                    project.customer?.name ||
                    getProjectStatusLabel(project.status)
                  }
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.purchaseOrders.length > 0 && (
        <SearchResultSection title="Purchase orders">
          {entityResults.purchaseOrders
            .slice(0, maxResults)
            .map((purchaseOrder: PurchaseOrder) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`purchase-orders-${purchaseOrder.key}`}
                  itemKey={`purchase-orders-${purchaseOrder.key}`}
                  url={`/purchase-orders/${purchaseOrder.key}`}
                  icon={<ShoppingCartOutlined fontSize="small" />}
                  primary={purchaseOrder.purchaseOrderNumber}
                  secondary={purchaseOrder.supplier?.name}
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.fulfillmentOrders.length > 0 && (
        <SearchResultSection title="Fulfillment orders">
          {entityResults.fulfillmentOrders
            .slice(0, maxResults)
            .map((fulfillmentOrder: FulfillmentOrder) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`fulfillment-orders-${fulfillmentOrder.key}`}
                  itemKey={`fulfillment-orders-${fulfillmentOrder.key}`}
                  url={`/fulfillments/${fulfillmentOrder.key}`}
                  icon={<ChecklistRtlOutlined fontSize="small" />}
                  primary={fulfillmentOrder.fulfillmentOrderNumber}
                  secondary={fulfillmentOrder.project?.name}
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.returnOrders.length > 0 && (
        <SearchResultSection title="Return orders">
          {entityResults.returnOrders
            .slice(0, maxResults)
            .map((returnOrder: ReturnOrder) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`return-orders-${returnOrder.key}`}
                  itemKey={`return-orders-${returnOrder.key}`}
                  url={`/return-orders/${returnOrder.key}`}
                  icon={<KeyboardReturnOutlined fontSize="small" />}
                  primary={returnOrder.returnOrderNumber}
                  secondary={RETURN_ORDER_REASON_LABELS[returnOrder.reason]}
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.inboundShipments.length > 0 && (
        <SearchResultSection title="Inbound shipments">
          {entityResults.inboundShipments
            .slice(0, maxResults)
            .map((shipment: Shipment) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`inbound-shipments-${shipment.key}`}
                  itemKey={`inbound-shipments-${shipment.key}`}
                  url={`/shipments?type=inbound&query=${encodeURIComponent(shipment.shipmentNumber)}`}
                  icon={<InventoryOutlined fontSize="small" />}
                  primary={shipment.shipmentNumber}
                  secondary={
                    shipment.purchaseOrder
                      ? `${shipment.purchaseOrder.purchaseOrderNumber}${shipment.purchaseOrder.supplier?.name ? ` (${shipment.purchaseOrder.supplier.name})` : ''}`
                      : undefined
                  }
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
      {entityResults.outboundShipments.length > 0 && (
        <SearchResultSection title="Outbound shipments">
          {entityResults.outboundShipments
            .slice(0, maxResults)
            .map((shipment: Shipment) => {
              const index = currentIndex++;
              return (
                <SearchResultItem
                  key={`outbound-shipments-${shipment.key}`}
                  itemKey={`outbound-shipments-${shipment.key}`}
                  url={`/shipments?type=outbound&query=${encodeURIComponent(shipment.shipmentNumber)}`}
                  icon={<LocalShippingOutlined fontSize="small" />}
                  primary={shipment.shipmentNumber}
                  secondary={
                    shipment.fulfillmentOrder?.project?.name
                      ? `Project: ${shipment.fulfillmentOrder.project.name}`
                      : undefined
                  }
                  isSelected={selectedIndex === index}
                  selectedItemRef={selectedItemRef}
                  onClick={handleResultClick}
                />
              );
            })}
        </SearchResultSection>
      )}
    </List>
  );
}
